import { isNullOrUndefined, isNullOrUndefinedOrEmpty } from 'rewire-common';
import { FilterComparative, FieldFilter, SortInput, ScheduleSort, IUdf } from '../DataTypes';
import { isDualValueComparative, isIntersectingFilterComparative } from '../utility/CustomFilterModel';
import isEmpty from '../utility/isEmpty';
import {
  FilterSpecialValueKey,
  getFilterSpecialValue,
  isFilterSpecialValue,
  ISchedulingRuleOptions,
} from './ScheduleRuleDialogModel';
import { Q } from '../utility/q';
import addESCustomFilters from '../utility/addESCustomFilters';
import isReferenceField from '../utility/isReferenceField';

const _fieldToAutoScheduleContextMap = {
  'job.id': '$context.jobId',
  earnedDate: '$context.earnedDate',
  effective: '$context.effective',
  'effective.start': '$context.effective.start',
  'effective.end': '$context.effective.end',
  'reason.id': '$context.reasonId',
  'area.id': '$context.areaId',
  'group.id': '$context.groupId',
  'occupation.id': '$context.occupationId',
  'template.id': '$context.templateId',
  'generator.id': '$context.generatorId',
  'job.position': '$context.position',
  units: '$context.units',
  position: '$context.position',
};

const _fieldToAutoScheduleSortMap: any = {
  employee: 'employee.name',
  occupation: 'occupation.name',
  position: 'position',
  area: 'area.name',
  group: 'group.name',
  generator: 'generator.name',
};

export type FilterFn = (fieldFilter: FieldFilter) => void;
export type ExpressionFilterFn = (expression: string) => void;

export interface IAutoSchedulePropertyMapField {
  field: string;
  internalField: string;
  index: string;
  category: string;
  udf?: IUdf;
  intersectsFieldNames?: { lowerField: string; upperField: string };
  mapToQuery?: (fieldFilter: FieldFilter) => FieldFilter;
  customFilter?: (
    addIncludeFilter: FilterFn,
    addIncludeExpressionFilter: ExpressionFilterFn,
    addExcludeFilter: FilterFn,
    fieldFilter: FieldFilter,
  ) => void;
}

export type AutoSchedulePropertyMap = Record<string, IAutoSchedulePropertyMapField>;

type FieldField = keyof typeof _fieldToAutoScheduleContextMap;
export default class AutoScheduleQueryMapper {
  protected static _mapFieldToContext(field: FieldField): string | undefined {
    return _fieldToAutoScheduleContextMap[field];
  }

  protected static _mapUdfFieldToContext(field: string, category: string): string | undefined {
    const newField = field.split('fields.')[1];
    return `$context.${category}Fields.${newField}`;
  }

  protected static _isExcludeComparative(comparative: FilterComparative) {
    return (
      comparative === FilterComparative.NotEquals ||
      comparative === FilterComparative.LessThan ||
      comparative === FilterComparative.LessThanOrEquals ||
      comparative === FilterComparative.NotIn ||
      comparative === FilterComparative.NotBetween ||
      comparative === FilterComparative.NotIntersects
    );
  }

  protected static _convertExcludeFilter(fieldFilter: FieldFilter, comparative: FilterComparative) {
    if (comparative === FilterComparative.NotEquals) {
      fieldFilter.eq = fieldFilter.ne;
      delete fieldFilter.ne;
    } else if (comparative === FilterComparative.NotIn) {
      fieldFilter.in = fieldFilter.nin;
      delete fieldFilter.nin;
    } else if (comparative === FilterComparative.NotBetween) {
      fieldFilter.between = fieldFilter.notBetween;
      delete fieldFilter.notBetween;
    } else if (comparative === FilterComparative.NotIntersects) {
      fieldFilter.intersects = fieldFilter.notIntersects;
      delete fieldFilter.notIntersects;
    } else if (comparative === FilterComparative.LessThan) {
      fieldFilter.gte = fieldFilter.lt;
      delete fieldFilter.lt;
    } else if (comparative === FilterComparative.LessThanOrEquals) {
      fieldFilter.gt = fieldFilter.lte;
      delete fieldFilter.lte;
    }
  }

  protected static _convertFilterValueInternal(
    value: any,
    comparative: FilterComparative,
    propertyMapField: IAutoSchedulePropertyMapField,
    dualFieldValueType?: 'lower' | 'upper',
  ): any {
    if (!isFilterSpecialValue(value)) {
      return value;
    }

    const specialValue = getFilterSpecialValue(value)!;
    if (specialValue.id === FilterSpecialValueKey.SelectedTimecardsValue) {
      let fieldName: any = propertyMapField.internalField;
      if (isIntersectingFilterComparative(comparative) && propertyMapField.intersectsFieldNames) {
        fieldName =
          dualFieldValueType === 'lower'
            ? propertyMapField.intersectsFieldNames.lowerField
            : propertyMapField.intersectsFieldNames.upperField;
      }
      const contextField = propertyMapField.udf
        ? AutoScheduleQueryMapper._mapUdfFieldToContext(fieldName, propertyMapField.category)
        : AutoScheduleQueryMapper._mapFieldToContext(fieldName);
      if (!isNullOrUndefinedOrEmpty(contextField)) {
        return contextField;
      }
    } else if (specialValue.id === FilterSpecialValueKey.SelectedTimecardsOccupation) {
      return '$context.occupationId';
    }

    return undefined;
  }

  protected static _convertFilterValue(
    fieldFilter: FieldFilter,
    propertyMapField: IAutoSchedulePropertyMapField,
    comparative: FilterComparative,
    value: any,
  ) {
    if (isDualValueComparative(comparative)) {
      const lower = AutoScheduleQueryMapper._convertFilterValueInternal(value.lower, comparative, propertyMapField, 'lower');
      const upper = AutoScheduleQueryMapper._convertFilterValueInternal(value.upper, comparative, propertyMapField, 'upper');
      fieldFilter[comparative] =
        !isNullOrUndefined(lower) || !isNullOrUndefined(upper) ? { ...value, lower: lower, upper: upper } : undefined;
    } else {
      fieldFilter[comparative] = AutoScheduleQueryMapper._convertFilterValueInternal(value, comparative, propertyMapField);
    }
  }

  protected static _getFilters(propertyMap: AutoSchedulePropertyMap, fieldFilters?: FieldFilter[]) {
    const includeFiltersByIndex = {} as Record<string, FieldFilter[]>;
    const excludeFiltersByIndex = {} as Record<string, FieldFilter[]>;
    const expressions: string[] = [];
    for (const f of fieldFilters || []) {
      let fieldFilter = { ...f };
      const propertyMapField = propertyMap[fieldFilter.field];
      const index = propertyMapField.index;
      if (propertyMapField?.internalField) fieldFilter.field = propertyMapField.internalField;
      let comparative = undefined as FilterComparative | undefined;
      let fieldFilterValue = undefined as any;

      if (propertyMapField?.mapToQuery) {
        fieldFilter = propertyMapField.mapToQuery(fieldFilter);
      }

      const addIncludeFilter = (fieldFilter: FieldFilter) => {
        if (!includeFiltersByIndex[index]) {
          includeFiltersByIndex[index] = [];
        }
        includeFiltersByIndex[index].push(fieldFilter);
      };

      const addExcludeFilter = (fieldFilter: FieldFilter) => {
        if (!excludeFiltersByIndex[index]) {
          excludeFiltersByIndex[index] = [];
        }
        excludeFiltersByIndex[index].push(fieldFilter);
      };

      const addIncludeExpressionFilter = (expression: string) => {
        expressions.push(expression);
      };

      if (propertyMapField?.customFilter) {
        propertyMapField.customFilter(addIncludeFilter, addIncludeExpressionFilter, addExcludeFilter, fieldFilter);
        continue;
      }

      for (const fc of Object.values(FilterComparative)) {
        if (!isEmpty(fieldFilter[fc])) {
          comparative = fc;
          fieldFilterValue = fieldFilter[fc];
          break;
        }
      }

      if (isNullOrUndefined(fieldFilterValue) || isNullOrUndefined(comparative)) {
        continue;
      }

      AutoScheduleQueryMapper._convertFilterValue(fieldFilter, propertyMapField, comparative, fieldFilterValue);

      if (isEmpty(fieldFilter[comparative])) {
        continue;
      }

      const isExcludeFilter = AutoScheduleQueryMapper._isExcludeComparative(comparative);
      if (isExcludeFilter) {
        AutoScheduleQueryMapper._convertExcludeFilter(fieldFilter, comparative);
      }

      if (isExcludeFilter) {
        addExcludeFilter(fieldFilter);
      } else {
        addIncludeFilter(fieldFilter);
      }
    }

    return { includeFiltersByIndex, excludeFiltersByIndex, expressions };
  }

  protected static _buildSorts(field: string, sortObj: any, sortArr: SortInput[]) {
    let sortField = _fieldToAutoScheduleSortMap[field];
    if (!sortField) {
      sortField = isReferenceField(field) ? `${field}.name` : field;
    }

    sortArr.push({ ...sortObj, field: sortField });
  }

  protected static _getSorts(sorts?: ScheduleSort) {
    if (!sorts) {
      return undefined;
    }
    const sortArr = Object.keys(sorts).reduce((_sortArr: SortInput[], field: keyof ScheduleSort) => {
      const sort = sorts[field];
      if (field === 'custom' && Array.isArray(sort)) {
        for (const cSort of sort) {
          AutoScheduleQueryMapper._buildSorts(cSort.field, cSort, _sortArr);
        }
      } else {
        AutoScheduleQueryMapper._buildSorts(field, sort, _sortArr);
      }
      return _sortArr;
    }, []);

    const prioritySort = (a: SortInput, b: SortInput) => (a.priority! < b.priority! ? -1 : a.priority! > b.priority! ? 1 : 0);
    const mappedSorts = [] as any[];
    for (const s of sortArr.sort(prioritySort)) {
      mappedSorts.push({ [s.field]: { order: s.direction } });
    }

    return mappedSorts;
  }

  protected static _getQueryObj(filtersByIndex: Record<string, FieldFilter[]>) {
    const queryObj = Object.keys(filtersByIndex).reduce(
      (prev, curr) => {
        const indexFilters = filtersByIndex[curr];
        prev[curr] = {
          query: Q<any>().bool((b) => b.filter((f) => addESCustomFilters(f, indexFilters))),
        };
        return prev;
      },
      {} as Record<string, unknown>,
    );

    return queryObj;
  }

  protected static _getExcludeQueryObj(filtersByIndex: Record<string, FieldFilter[]>) {
    const queryObj = Object.keys(filtersByIndex).reduce(
      (prev, curr) => {
        const indexFilters = filtersByIndex[curr];
        prev[curr] = {
          query: Q<any>().bool((b) => b.filter((f) => [f.bool((_s) => b.should((s) => addESCustomFilters(s, indexFilters)))])),
        };
        return prev;
      },
      {} as Record<string, unknown>,
    );

    return queryObj;
  }

  public static getQuery(propertyMap: AutoSchedulePropertyMap, schedulingRuleData: ISchedulingRuleOptions) {
    const { includeFiltersByIndex, excludeFiltersByIndex, expressions } =
      AutoScheduleQueryMapper._getFilters(propertyMap, schedulingRuleData.filters?.custom) ?? {};

    const queryObj: { include?: any; exclude?: any; sort?: any } = {};
    if (includeFiltersByIndex && Object.keys(includeFiltersByIndex).length) {
      queryObj['include'] = AutoScheduleQueryMapper._getQueryObj(includeFiltersByIndex);
    }
    if (excludeFiltersByIndex && Object.keys(excludeFiltersByIndex).length) {
      queryObj['exclude'] = AutoScheduleQueryMapper._getExcludeQueryObj(excludeFiltersByIndex);
    }
    if (expressions.length) {
      if (!queryObj['include']) {
        queryObj['include'] = {};
      }
      queryObj['include']['expressions'] = expressions;
    }

    const sorts = AutoScheduleQueryMapper._getSorts(schedulingRuleData.sorts);
    if (sorts?.length) {
      queryObj['sort'] = sorts;
    }

    if (!Object.keys(queryObj).length) {
      return undefined;
    }

    return JSON.stringify(queryObj);
  }
}
